import React, { useState, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { styledDefaultColDef } from '../../extras/cellRenderers';
import { getStudentGroups, getStudentGroupGlobalData } from '../../services/apiService';
import StudentGroupEditModal from '../../pages/student-groups/_components/StudentGroupEditModal';

const PAGE_SIZE = 10;

function ActionCell(props) {
  const { data, onViewSessions, setOpenEditModal, setSelectedRowData } = props;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
      <button
        onClick={() => onViewSessions(data)}
        style={{
          border: '1px solid #E5E7EB',
          background: '#fff',
          color: '#4F46E5',
          borderRadius: 4,
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        View Sessions
      </button>
      <button
        onClick={() => {
          setSelectedRowData(data);
          setOpenEditModal(true);
        }}
        style={{
          border: '1px solid #E5E7EB',
          background: '#fff',
          color: '#374151',
          borderRadius: 4,
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Edit
      </button>
    </div>
  );
}

function ScheduleCssTable({ onViewSessions, refreshCounter }) {
  const [gridApi, setGridApi] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [globalData, setGlobalData] = useState({
    cohort: [],
    almaXCohort: [],
    program: { course: [], plan: [], subplan: [] },
  });

  useEffect(() => {
    const fetchGlobals = async () => {
      try {
        const res = await getStudentGroupGlobalData();
        const data = res?.data || res;
        setGlobalData({
          cohort: Array.isArray(data?.cohort) ? data.cohort : [],
          almaXCohort: Array.isArray(data?.almaXCohort) ? data.almaXCohort : [],
          program: {
            course: Array.isArray(data?.program?.course) ? data.program.course : [],
            plan: Array.isArray(data?.program?.plan) ? data.program.plan : [],
            subplan: Array.isArray(data?.program?.subplan) ? data.program.subplan : [],
          },
        });
      } catch (e) {
        console.error(e);
      }
    };
    fetchGlobals();
  }, []);

  const getServerSideDatasource = () => ({
    getRows: async (params) => {
      const { startRow } = params.request || params;
      const page = Math.floor((startRow || 0) / PAGE_SIZE) + 1;
      try {
        const response = await getStudentGroups(page);
        if (response?.data) {
          const mappedData = response.data?.data?.map((item) => ({
            id: item.id,
            groupName: item.groupName || 'N/A',
            groupType:
              item.groupType && item.params?.type
                ? `${item.groupType}-${item.params.type}`
                : item.groupType || 'N/A',
            params: item.params || {},
            groupDecr: item.groupDecr || 'N/A',
            createdAt: item.createdAt || null,
            createdBy: item.createdBy || 'N/A',
            updatedAt: item.updatedAt || null,
            updatedBy: item.updatedBy || 'N/A',
            deletedAt: item.deletedAt || null,
          }));
          params.successCallback(mappedData, response.data?.totalRecords);
        } else {
          params.successCallback([], 0);
        }
      } catch (error) {
        console.error(error);
        params.failCallback();
      }
    },
  });

  const refreshData = () => {
    if (gridApi) gridApi.setServerSideDatasource(getServerSideDatasource());
  };

  useEffect(() => {
    if (gridApi) refreshData();
  }, [refreshCounter]); // eslint-disable-line

  const onGridReady = (params) => {
    setGridApi(params.api);
    params.api.setServerSideDatasource(getServerSideDatasource());
    params.api.sizeColumnsToFit();
  };

  const columnDefs = useMemo(
    () => [
      { headerName: 'Group Name', field: 'groupName', flex: 1 },
      { headerName: 'Group Type', field: 'groupType', width: 130 },
      {
        headerName: 'Params',
        field: 'params',
        width: 250,
        valueGetter: (params) => {
          const p = params?.data?.params;
          if (p && typeof p === 'object') {
            const userCount =
              p.userIdLength === 0 ? p.userEmailLength :
              p.userEmailLength === 0 ? p.userIdLength :
              p.userEmailLength;
            return `Type: ${p.type || 'N/A'}, Users: ${userCount || 0}`;
          }
          return 'N/A';
        },
      },
      { headerName: 'Group Description', field: 'groupDecr', flex: 1 },
      {
        headerName: 'Created At',
        field: 'createdAt',
        width: 140,
        valueGetter: (params) => {
          const d = params?.data?.createdAt;
          return d ? new Date(d).toLocaleDateString() : 'N/A';
        },
      },
      { headerName: 'Created By', field: 'createdBy', width: 180 },
      {
        headerName: 'Deleted At',
        field: 'deletedAt',
        width: 140,
        valueGetter: (params) => {
          const d = params?.data?.deletedAt;
          return d ? new Date(d).toLocaleDateString() : 'NA';
        },
      },
      {
        headerName: 'Actions',
        field: 'action',
        width: 230,
        pinned: 'right',
        sortable: false,
        cellRenderer: ActionCell,
        cellRendererParams: {
          onViewSessions,
          setOpenEditModal,
          setSelectedRowData,
        },
      },
    ],
    [onViewSessions] // eslint-disable-line
  );

  const defaultColDef = useMemo(
    () => ({ ...styledDefaultColDef, filter: true }),
    []
  );

  return (
    <>
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.07)',
          boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <div className="ag-theme-alpine" style={{ height: 630, width: '100%' }}>
          <AgGridReact
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            rowHeight={52}
            rowModelType="serverSide"
            serverSideStoreType="partial"
            animateRows
            serverSideInfiniteScroll
            pagination
            paginationPageSize={PAGE_SIZE}
            cacheBlockSize={PAGE_SIZE}
            suppressPaginationPanel={false}
            domLayout="normal"
          />
        </div>
      </div>

      {openEditModal && (
        <StudentGroupEditModal
          openEditModal={openEditModal}
          setOpenEditModal={() => {
            setOpenEditModal(false);
            setSelectedRowData(null);
          }}
          onRefresh={refreshData}
          rowData={selectedRowData}
          cohortListProp={globalData.cohort}
          almaxCohortListProp={globalData.almaXCohort}
          programOptionsProp={globalData.program}
        />
      )}
    </>
  );
}

export default ScheduleCssTable;
